import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layout/DashboardLayout";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  TablePagination,
  Alert,
} from "@mui/material";
import { getAuditLog } from "../../api/auth";
import { brand } from "../../theme";
import { EmptyState } from "../../components/EmptyState";
import HistoryIcon from "@mui/icons-material/History";
import { ROLES } from "../../constants/roles";

const ACTION_LABELS = {
  "kyc.submitted": "KYC Submitted",
  "pcc.processed": "PCC Processed",
  "contract.signed": "Contract Signed",
  "admin.invited": "Admin Invited",
};

const ACTION_COLORS = {
  "kyc.submitted": "info",
  "pcc.processed": "warning",
  "contract.signed": "success",
  "admin.invited": "secondary",
};

const ROLE_COLORS = {
  [ROLES.SUPER_ADMIN]: brand.black,
  [ROLES.HELPDESK]: brand.terracotta,
  [ROLES.SALES_ACCOUNT_SPECIALIST]: "#16a34a",
  [ROLES.TECHNICAL_SUPPORT_SPECIALIST]: brand.goldDark,
  [ROLES.AGENCY_OWNER]: brand.grayDark,
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setError(null);
    (async () => {
      try {
        const params = actionFilter !== "all" ? { action: actionFilter } : {};
        const res = await getAuditLog(params);
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error("Failed to fetch audit log:", err);
        setError("Failed to load audit log. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [actionFilter]);

  const handleFilterChange = (_, value) => {
    if (value !== null) {
      setLoading(true);
      setPage(0);
      setActionFilter(value);
    }
  };

  return (
    <DashboardLayout>
      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Audit Log
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          A record of all sensitive actions taken in the system.
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={actionFilter}
        exclusive
        onChange={handleFilterChange}
        size="small"
        sx={{ mb: 3, flexWrap: "wrap", gap: 0.5 }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="kyc.submitted">KYC Submitted</ToggleButton>
        <ToggleButton value="pcc.processed">PCC Processed</ToggleButton>
        <ToggleButton value="contract.signed">Contract Signed</ToggleButton>
        <ToggleButton value="admin.invited">Admin Invited</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />}
          description={
            actionFilter === "all"
              ? "No log entries yet."
              : "No log entries match this filter."
          }
          action={
            actionFilter !== "all"
              ? { label: "Clear filter", onClick: () => handleFilterChange(null, "all") }
              : undefined
          }
        />
      ) : (
        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Actor</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Target</strong></TableCell>
                <TableCell><strong>Details</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(log.createdAt).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {log.actor?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {log.actor?.email || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.actorRole?.replace(/_/g, " ")}
                      size="small"
                      sx={{
                        bgcolor: ROLE_COLORS[log.actorRole] || "#6b7280",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ACTION_LABELS[log.action] || log.action}
                      color={ACTION_COLORS[log.action] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.targetName}</TableCell>
                  <TableCell>
                    {log.meta
                      ? Object.entries(log.meta)
                          .filter(([, v]) => v != null)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ")
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}
    </DashboardLayout>
  );
}
